import { ConsoleLogger } from '@proposify/frontend-observability';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import IcoFontColor from 'components/icon/icons/IcoFontColor';
import { useEffect, useRef, useState } from 'react';
import { COLORS } from '../../../../../muiTheme/colors.ts';
import type { ColorResult } from '../../../shared/components/ColorPicker';
import { ColorPopup } from '../../../shared/components/ColorPicker';
import { useActiveEditor } from '../ActiveEditorContext';
import { PyMuiIconButton } from '../components/generalComponents';
import { selectAllTextDefaultStyles } from '../../../grid/reduxStore/textDefaultStylingSlice.ts';
import { useAppSelector } from '../../../grid/reduxStore/Store.ts';
import { getDocumentColors } from '../../../grid/reduxStore/selectors/documentColorsSelector';
import { createTextStyleHandler, getNodeTypeFromSelection, getParentAttribute } from '../helpers/textStyleHelpers.ts';
import type { Editor } from '@tiptap/react';
import type { Transaction } from '@tiptap/pm/state';
import type { Node } from '@tiptap/pm/model';
import { useGetTipTapToolbarStatus } from '../hooks/useGetTipTapToolbarStatus.ts';
import type { colorParams } from 'components/editor/shared/components/ColorPicker/types.ts';

const CustomFillWrapper = styled(Box)<{ color?: string; disabled?: boolean }>`
  svg path:nth-of-type(1) {
    fill: ${(props) => (props.disabled ? COLORS.disabledTiptapIcon : props.color || COLORS.colorHeaderRow)};
  }

  svg path:last-child {
    fill: rgba(0, 40, 58, 0.87);
  }
`;
const ATTRIBUTE_NAME = 'color';

const TextColor = () => {
  const { activeEditor: editor } = useActiveEditor();
  const textDefaultStyles = useAppSelector(selectAllTextDefaultStyles);
  const iconRef = useRef<HTMLSpanElement>(null);
  // Kept independent of the toolbar's disabled/comments-mode state to avoid changing existing production behavior.
  const isBlockInEditMode = !useGetTipTapToolbarStatus();

  function getDefaultTextColor() {
    if (!editor) return;
    try {
      const parentValue = getNodeTypeFromSelection(editor);
      return editor.getAttributes('textStyle')?.[ATTRIBUTE_NAME] || textDefaultStyles?.[parentValue]?.[ATTRIBUTE_NAME];
    } catch (e) {
      ConsoleLogger.error('Error getting default text color', e);
      return COLORS?.colorHeaderRow || '';
    }
  }

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [buttonColor, setButtonColor] = useState<string>(getDefaultTextColor());
  const [documentColors, setDocumentColors] = useState<colorParams[] | undefined>(undefined);

  const setParsedSelectedValue = (value) => {
    setButtonColor(value);
  };

  const getColorParentAttribute = (parent: Node | null) => getParentAttribute(parent, ATTRIBUTE_NAME, textDefaultStyles);

  useEffect(() => {
    const updateSelectedSize = createTextStyleHandler({
      attributeName: ATTRIBUTE_NAME,
      getParentAttribute: getColorParentAttribute,
      parseValue: (value) => value,
    });

    const callUpdateSelectedColor = (event: { editor: Editor; transaction: Transaction }) => {
      updateSelectedSize(event, setParsedSelectedValue);
    };

    editor?.on('selectionUpdate', callUpdateSelectedColor);
    editor?.on('transaction', callUpdateSelectedColor);
    return () => {
      editor?.off('selectionUpdate', callUpdateSelectedColor);
      editor?.off('transaction', callUpdateSelectedColor);
    };
  }, [editor, textDefaultStyles]);

  useEffect(() => {
    if (!editor) return;

    const nodeType = getNodeTypeFromSelection(editor);
    const currentDefault = textDefaultStyles?.[nodeType]?.[ATTRIBUTE_NAME];
    const color = editor.getAttributes?.('textStyle')?.[ATTRIBUTE_NAME];

    if (!color && buttonColor !== currentDefault) {
      setButtonColor(currentDefault);
    }
  }, [textDefaultStyles, editor, buttonColor]);

  const handleOpenMenu = () => {
    setDocumentColors(getDocumentColors());
    setIsModalOpen(true);
  };

  function changeBlockBackgroundColor(color: string): void {
    editor?.chain().focus().setColor(color).run();
    setIsModalOpen(false);
  }

  const onCloseHandler = (color: ColorResult, isValueSet: boolean) => {
    isValueSet ? changeBlockBackgroundColor(color) : setIsModalOpen(false);
  };

  return (
    <>
      <PyMuiIconButton disabled={isBlockInEditMode} attributeName="text-color" onClick={handleOpenMenu} isActive={isModalOpen}>
        <span ref={iconRef} data-testid={'text-color'}>
          <CustomFillWrapper disabled={isBlockInEditMode} color={buttonColor}>
            <IcoFontColor style={{ padding: '3px 2px 0 2px' }} />
          </CustomFillWrapper>
        </span>
      </PyMuiIconButton>
      {
        <ColorPopup
          buttonRef={iconRef}
          currentColor={buttonColor}
          onClose={onCloseHandler}
          open={isModalOpen}
          presetColors={documentColors}
        />
      }
    </>
  );
};

export default TextColor;
